import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Image from 'components/Common/Image'
import TextLink from 'components/Luxkit/TextLink'
import CurrencyContext from 'contexts/currencyContext'
import { getBNPLMethodAvailability } from 'lib/payment/paymentUtils'
import useToggle from 'hooks/useToggle'
import InterestFreeLearnMoreModal from 'components/Common/Modals/InterestFreeLearnMoreModal/InterestFreeLearnMoreModal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'

const Root = styled(Pane)`
  text-align: center;
`

const Logos = styled(Group)`
  margin-top: ${rem(16)};
`

const LogoImages = styled(Image)`
    width: 100%;
    max-width: ${rem(130)};
    max-height: ${rem(40)};

    ${mediaQueryUp.tablet} {
      max-width: ${rem(90)};
    }
`

interface Props {
  className?: string;
  onLearnMore?: () => void;
  prices: Array<number> | number;
}

function OfferBNPLAd(props: Props) {
  const {
    className,
    prices,
    onLearnMore,
  } = props

  const [modalOpened, onInterestFreeModalClose, showInterestFreeModal] = useToggle(false)
  const currency = useContext(CurrencyContext)

  const handleOnLearnMore = useCallback(() => {
    showInterestFreeModal()
    onLearnMore?.()
  }, [onLearnMore, showInterestFreeModal])

  const bnplMethods = useMemo(() => {
    const allPrices = Array.isArray(prices) ? prices : [prices]
    return getBNPLMethodAvailability(currency, ...allPrices)
  }, [prices, currency])
  const hasAnyMethods = useMemo(() => Object.values(bnplMethods).some(Boolean), [bnplMethods])

  if (!hasAnyMethods) {
    return null
  }

  return (<>
    <InterestFreeLearnMoreModal
      isOpen={modalOpened}
      onClose={onInterestFreeModalClose}
    />
    <Root className={className}>
      <PaneBody>
        <BodyText variant="small" weight="bold">
          Interest-free payment options available&nbsp;
          <TextLink onClick={handleOnLearnMore}>
            Learn more
          </TextLink>
        </BodyText>
        <Logos direction="horizontal" gap={16} tabletGap={8} horizontalAlign="center" verticalAlign="center">
          {bnplMethods.isKlarnaAvailable || bnplMethods.isKlarnaBpAvailable && (
            <LogoImages id="ph5b1cik9gdapegknwl" alt="Klarna Pay" />
          )}
          {bnplMethods.isAfterPayBpAvailable && (
            <LogoImages id="2kac81byj4yt5abm0nr" alt="After Pay" />
          )}
          {bnplMethods.isHoolahBpAvailable && (
            <LogoImages id="shopback-logo" alt="Shopback" />
          )}
          {bnplMethods.isAtomeBpAvailable && (
            <LogoImages id="atome-logo" alt="Atome" />
          )}
          {bnplMethods.isWaaveBpAvailable && (
            <LogoImages id="luxpay-logo" alt="LuxPay" />
          )}
        </Logos>
      </PaneBody>
    </Root>
  </>

  )
}

export default React.memo(OfferBNPLAd)
